import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, FileText, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const MAX_BYTES = 20 * 1024 * 1024;
const ALLOWED = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
];

const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const numberOrNull = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const emptyForm = {
  requester: "",
  due_start: "",
  amount: "",
  notes: "",
};

export function DeductionFormDialog({
  open,
  onOpenChange,
  employee,
  deduction,
  onSaved,
}) {
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [removeExistingFile, setRemoveExistingFile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (deduction) {
      setForm({
        requester: deduction.requester || "",
        due_start: deduction.due_start || "",
        amount: deduction.amount ?? "",
        notes: deduction.notes || "",
      });
    } else {
      setForm(emptyForm);
    }
    setFile(null);
    setRemoveExistingFile(false);
    setErr("");
  }, [open, deduction]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      setErr("Nem támogatott fájltípus. PDF, JPG, PNG vagy WebP fájlt tölts fel.");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_BYTES) {
      setErr(`A fájl mérete meghaladja a maximumot (${MAX_BYTES / (1024 * 1024)} MB).`);
      e.target.value = "";
      return;
    }
    setErr("");
    setFile(f);
    setRemoveExistingFile(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.requester.trim()) {
      setErr("A kérelmező megadása kötelező.");
      return;
    }
    if (!form.due_start) {
      setErr("Az esedékesség kezdete kötelező.");
      return;
    }
    if (form.amount === "" || !Number.isFinite(Number(form.amount))) {
      setErr("Az összeg megadása kötelező.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        requester: form.requester.trim(),
        due_start: form.due_start,
        amount: numberOrNull(form.amount),
        notes: form.notes || null,
      };
      let saved;
      let dedId;
      if (deduction) {
        const { data } = await api.patch(`/deductions/${deduction._id}`, payload);
        saved = data;
        dedId = data._id;
      } else {
        if (!employee?._id) throw new Error("Hiányzó munkavállaló.");
        const { data } = await api.post(
          `/employees/${employee._id}/deductions`,
          payload
        );
        saved = data;
        dedId = data._id;
      }

      if (removeExistingFile && deduction?.file_url) {
        const { data } = await api.delete(`/deductions/${dedId}/file`);
        saved = data;
      }

      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(`/deductions/${dedId}/file`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        saved = data;
      }

      toast.success(deduction ? "Levonás frissítve." : "Levonás rögzítve.");
      onOpenChange(false);
      if (onSaved) onSaved(saved);
    } catch (error) {
      setErr(formatApiError(error?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  const existingHasFile =
    !!deduction?.file_url && !removeExistingFile && !file;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="deduction-dialog"
        className="sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle>
            {deduction ? "Levonás szerkesztése" : "Új levonás"}
          </DialogTitle>
          <DialogDescription>
            Add meg a bért érintő levonás részleteit.
            {employee && (
              <>
                {" "}
                <span className="font-medium text-foreground">
                  {`${employee.last_name || ""} ${employee.first_name || ""}`.trim()}
                </span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <Field label="Kérelmező *" testId="ded-requester">
            <Input
              value={form.requester}
              onChange={(e) =>
                setForm((f) => ({ ...f, requester: e.target.value }))
              }
              placeholder="pl. NAV, Bíróság, Bank"
              maxLength={200}
            />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Esedékesség kezdete *" testId="ded-due-start">
              <DateInput
                value={form.due_start}
                onChange={(v) =>
                  setForm((f) => ({ ...f, due_start: v }))
                }
              />
            </Field>
            <Field label="Összeg (Ft) *" testId="ded-amount">
              <Input
                type="number"
                inputMode="numeric"
                step="1"
                min="0"
                value={form.amount}
                onChange={(e) =>
                  setForm((f) => ({ ...f, amount: e.target.value }))
                }
                placeholder="0"
                className="font-mono"
              />
            </Field>
          </div>

          <Field label="Megjegyzés" testId="ded-notes">
            <Textarea
              value={form.notes}
              onChange={(e) =>
                setForm((f) => ({ ...f, notes: e.target.value }))
              }
              rows={2}
            />
          </Field>

          <Field label="Dokumentum (PDF/JPG/PNG/WebP)" testId="ded-file">
            <input
              ref={fileRef}
              type="file"
              accept={ALLOWED.join(",")}
              className="hidden"
              onChange={onPickFile}
              data-testid="ded-file-input"
            />
            {file ? (
              <div
                data-testid="ded-file-selected"
                className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2.5"
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{file.name}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {formatBytes(file.size)}
                  </p>
                </div>
                <button
                  type="button"
                  data-testid="ded-file-clear"
                  onClick={() => {
                    setFile(null);
                    if (fileRef.current) fileRef.current.value = "";
                  }}
                  className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                  aria-label="Fájl eltávolítása"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : existingHasFile ? (
              <div
                data-testid="ded-file-existing"
                className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2.5"
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {deduction.file_name || "csatolt dokumentum"}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {formatBytes(deduction.file_size)} · csatolt
                  </p>
                </div>
                <button
                  type="button"
                  data-testid="ded-file-replace"
                  onClick={() => fileRef.current?.click()}
                  className="rounded-md border border-border px-2 py-1 text-[11px] text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                >
                  Cserélés
                </button>
                <button
                  type="button"
                  data-testid="ded-file-remove"
                  onClick={() => setRemoveExistingFile(true)}
                  className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                  aria-label="Csatolmány eltávolítása"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                data-testid="ded-file-button"
                onClick={() => fileRef.current?.click()}
                className={cn(
                  "flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-muted/20 px-3 py-3 text-sm text-muted-foreground transition-colors",
                  "hover:border-foreground/30 hover:bg-muted/40 hover:text-foreground"
                )}
              >
                <Upload className="h-4 w-4" strokeWidth={1.75} />
                Dokumentum kiválasztása
              </button>
            )}
          </Field>

          {err && (
            <div
              data-testid="ded-error"
              className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
            >
              {err}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="ded-cancel"
            >
              Mégse
            </Button>
            <Button type="submit" disabled={saving} data-testid="ded-submit">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Mentés…
                </>
              ) : deduction ? (
                "Mentés"
              ) : (
                "Rögzítés"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block space-y-1.5" data-testid={testId}>
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

export function formatDateHu(value) {
  if (!value) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return value;
  return `${m[1]}. ${parseInt(m[2], 10)}. ${parseInt(m[3], 10)}.`;
}
